// Pure helpers for the Covers feature. No Firebase imports so they can be unit tested.

const toMs = (v) => {
  if (!v) return NaN;
  if (typeof v.toDate === "function") return v.toDate().getTime();
  return new Date(v).getTime();
};

export const lessonStartMs = (lesson) => toMs(lesson.startDateTime);
export const lessonEndMs = (lesson) => toMs(lesson.endDateTime);

export const lessonsOverlap = (a, b) =>
  lessonStartMs(a) < lessonEndMs(b) && lessonStartMs(b) < lessonEndMs(a);

/** True if `lesson` overlaps any lesson in `others` (excluding itself). */
export const overlapsAny = (lesson, others) =>
  others.some((o) => o.id !== lesson.id && lessonsOverlap(lesson, o));

/** True if any two lessons in the list overlap each other. */
export const hasInternalOverlap = (lessons) =>
  lessons.some((a, i) => lessons.slice(i + 1).some((b) => lessonsOverlap(a, b)));

export const lessonDurationHours = (lesson) =>
  (lessonEndMs(lesson) - lessonStartMs(lesson)) / 3600000;

/**
 * Group lessons into blocks: same original tutor, consecutive in time
 * (next starts within `gapMinutes` of the previous ending).
 * Returns blocks sorted by start; each block's lessons are sorted by start.
 */
export const groupIntoBlocks = (lessons, gapMinutes = 0, { byTutor = true } = {}) => {
  const gapMs = gapMinutes * 60000;
  const sorted = [...lessons].sort((a, b) => {
    const t = lessonStartMs(a) - lessonStartMs(b);
    return t !== 0 ? t : (a.tutorId || "").localeCompare(b.tutorId || "");
  });

  const buckets = new Map();
  for (const lesson of sorted) {
    const key = byTutor ? lesson.tutorId || "__none__" : "__all__";
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(lesson);
  }

  const blocks = [];
  for (const [tutorId, tutorLessons] of buckets) {
    let current = null;
    for (const lesson of tutorLessons) {
      if (current && lessonStartMs(lesson) - current.endMs <= gapMs) {
        current.lessons.push(lesson);
        current.endMs = Math.max(current.endMs, lessonEndMs(lesson));
      } else {
        current = {
          tutorId: byTutor && tutorId !== "__none__" ? tutorId : null,
          tutorName: lesson.tutorName || "Unassigned",
          startMs: lessonStartMs(lesson),
          endMs: lessonEndMs(lesson),
          lessons: [lesson],
        };
        blocks.push(current);
      }
    }
  }

  for (const block of blocks) {
    const names = [];
    const ids = [];
    for (const l of block.lessons) {
      const id = l.tutorId || "";
      if (!ids.includes(id)) {
        ids.push(id);
        names.push(l.tutorName || "Unassigned");
      }
    }
    block.tutorIds = ids;
    block.tutorNames = names;
    block.mixed = ids.length > 1;
    if (block.mixed) block.tutorName = names.join(", ");
    block.id = `${byTutor ? block.tutorId || "none" : "time"}_${block.startMs}`;
    block.hours = (block.endMs - block.startMs) / 3600000;
    block.locationNames = [...new Set(block.lessons.map((l) => l.locationName).filter(Boolean))];
  }

  return blocks.sort((a, b) => a.startMs - b.startMs);
};

/**
 * Work out which tutors could cover a set of sessions.
 * Returns every tutor (except the original) with `suggested` and `reasons`.
 *
 * @param sessions   lessons needing cover
 * @param tutors     [{ id, firstName, lastName, capabilities, blockedStudents, role }]
 * @param otherLessons lessons on the same day(s) for clash detection (any tutor)
 * @param originalTutorId excluded from results
 */
export const suggestTutors = ({
  sessions,
  tutors,
  otherLessons = [],
  originalTutorId,
  originalTutorIds,
  hideSensitiveReasons = false,
}) => {
  const excluded = new Set([
    ...(originalTutorIds || []),
    ...(originalTutorId ? [originalTutorId] : []),
    ...sessions.map((s) => s.tutorId).filter(Boolean),
  ]);
  const sessionIds = new Set(sessions.map((s) => s.id));
  const neededGroups = [...new Set(sessions.map((s) => s.subjectGroupId).filter(Boolean))];
  const groupNames = new Map(sessions.map((s) => [s.subjectGroupId, s.subjectGroupName]));
  const studentIds = new Set(sessions.flatMap((s) => s.studentIds || []));

  return tutors
    .filter((t) => !excluded.has(t.id))
    .map((t) => {
      const reasons = [];
      const caps = new Set(t.capabilities || []);
      const missing = neededGroups.filter((g) => !caps.has(g));
      if (missing.length) {
        reasons.push(
          `No capability for ${missing.map((g) => groupNames.get(g) || g).join(", ")}`
        );
      }

      const blocked = (t.blockedStudents || []).filter((sid) => studentIds.has(sid));
      if (blocked.length) {
        const names = sessions
          .flatMap((s) => (s.studentIds || []).map((sid, i) => [sid, s.studentNames?.[i]]))
          .filter(([sid]) => blocked.includes(sid))
          .map(([, name]) => name)
          .filter(Boolean);
        reasons.push(
          hideSensitiveReasons
            ? "Other reason"
            : `Blocked student${blocked.length > 1 ? "s" : ""}: ${[...new Set(names)].join(", ") || blocked.length}`
        );
      }

      const clashes = otherLessons.filter(
        (l) =>
          l.tutorId === t.id &&
          !sessionIds.has(l.id) &&
          l.type !== "Cancelled" &&
          sessions.some((s) => lessonsOverlap(s, l))
      );
      if (clashes.length) {
        const first = clashes[0];
        const fmt = (ms) =>
          new Date(ms).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });
        reasons.push(
          `Has a lesson ${fmt(lessonStartMs(first))}–${fmt(lessonEndMs(first))}${
            clashes.length > 1 ? ` (+${clashes.length - 1} more)` : ""
          }`
        );
      }

      return {
        id: t.id,
        name: `${t.firstName || ""} ${t.lastName || ""}`.trim(),
        role: t.role,
        tutorColor: t.tutorColor,
        suggested: reasons.length === 0,
        reasons,
      };
    })
    .sort((a, b) => {
      if (a.suggested !== b.suggested) return a.suggested ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
};
